import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../../views/components/common/SkuCopyRow";
import CustomTextField from "../../../../../../../views/components/common/textFields/TextField";
import AppTablePagenation from "../../../../../../../views/components/common/pages/AppTablePagenation";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import { useDebounce } from "../../../../../../../views/components/common/hooks/Debounce";
import { formatDate } from "../../../../../../../views/components/common/functions/formatDate";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
  modernSectionPaperSx,
} from "../../../../../../../styles/modernListingPageSx";
import {
  LEDGER_IN_TYPES,
  LEDGER_OUT_TYPES,
  PRODUCT_TRANSACTIONS_PAGE_SIZE,
  ProductTransactionRow,
} from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";

type LedgerDirection = "in" | "out" | "neutral";

const getDirection = (row: ProductTransactionRow): LedgerDirection => {
  if (LEDGER_IN_TYPES.includes(row.log_type)) return "in";
  if (LEDGER_OUT_TYPES.includes(row.log_type)) return "out";
  return "neutral";
};

interface DetailCellProps {
  label: string;
  value?: React.ReactNode;
  /** When set, renders the SKU with a copy button instead of `value`. */
  sku?: string | null;
  skuSuffix?: string;
}

const DetailCell: React.FC<DetailCellProps> = ({
  label,
  value,
  sku,
  skuSuffix,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  return (
    <Grid2 size={{ xs: 6, sm: 4 }}>
      <CustomTypography variant="body3" color={colors.subtitle}>
        {label}
      </CustomTypography>
      {sku !== undefined ? (
        <SkuCopyRow
          sku={sku}
          suffix={skuSuffix}
          emptyFallback="—"
          fontWeight={600}
          color={colors.title}
        />
      ) : (
        <CustomTypography
          variant="body2"
          fontWeight={600}
          color={colors.title}
          sx={{ wordBreak: "break-word" }}
        >
          {value ?? "—"}
        </CustomTypography>
      )}
    </Grid2>
  );
};

const withRaisedBy = (id: number | null, raisedBy: string | null) =>
  id ? `#${id}${raisedBy ? ` · ${raisedBy}` : ""}` : null;

const TransactionCard: React.FC<{ row: ProductTransactionRow }> = ({ row }) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const colors = getModernPageColors(theme);
  const direction = getDirection(row);
  const qty = Number(row.quantity) || 0;

  const tone =
    direction === "in"
      ? { fg: c.successGreen, bg: c.successGreenLightBackground }
      : direction === "out"
        ? { fg: c.errorRed, bg: theme.palette.blushPink.main }
        : { fg: c.purpleMain, bg: c.lightPurple };

  const pillSx = { height: 22, fontSize: "0.7rem", fontWeight: 700 };
  const isBoxTransfer = Boolean(row.from_box_id || row.to_box_id);

  return (
    <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              label={row.activity_type || row.log_type}
              sx={{ ...pillSx, color: tone.fg, bgcolor: tone.bg }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`#${row.log_id}`}
              sx={pillSx}
            />
          </Box>
          <CustomTypography
            variant="body3"
            color={colors.subtitle}
            sx={{ mt: 1 }}
          >
            {formatDate(row.log_date)}
            {row.logged_by ? ` · ${row.logged_by}` : ""}
          </CustomTypography>
        </Box>
        <Box textAlign={{ xs: "left", sm: "right" }}>
          <CustomTypography variant="heading5" color={tone.fg}>
            {direction === "in" ? "+" : direction === "out" ? "−" : ""}
            {qty}
          </CustomTypography>
          {direction === "neutral" && (
            <CustomTypography variant="body3" color={colors.subtitle}>
              Blocked stock only
            </CustomTypography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Grid2 container spacing={1.5}>
        <DetailCell label="SKU" sku={row.sku} />
        <DetailCell label="Warehouse" value={row.warehouse_id} />
        <DetailCell label="Box" value={row.box_id} />
        <DetailCell label="Current Rack" value={row.rack} />
        {isBoxTransfer && (
          <DetailCell
            label="Box Move"
            value={`${row.from_box_id ?? "—"} → ${row.to_box_id ?? "—"}`}
          />
        )}
        {row.expiry_date && (
          <DetailCell
            label="Expiry"
            value={
              dayjs(row.expiry_date).isValid()
                ? dayjs(row.expiry_date).format("DD MMM YYYY")
                : row.expiry_date
            }
          />
        )}
        {row.pr_id && (
          <DetailCell
            label="PR"
            value={withRaisedBy(row.pr_id, row.pr_raised_by)}
          />
        )}
        {row.po_id && (
          <DetailCell
            label="PO"
            value={withRaisedBy(row.po_id, row.po_raised_by)}
          />
        )}
        {row.order_id && (
          <DetailCell label="Order" value={`#${row.order_id}`} />
        )}
        {Number(row.quantity_rejected) > 0 && (
          <DetailCell label="Rejected" value={row.quantity_rejected} />
        )}
      </Grid2>

      {row.rejection_reason && (
        <CustomTypography
          variant="body3"
          color={colors.subtitle}
          sx={{ mt: 1, wordBreak: "break-word" }}
        >
          Rejection reason: {row.rejection_reason}
        </CustomTypography>
      )}
    </Paper>
  );
};

interface SwageazyInventoryTransactionsDrawerProps {
  store: SwageazyInventoryAdjustmentStore;
}

const SwageazyInventoryTransactionsDrawer: React.FC<
  SwageazyInventoryTransactionsDrawerProps
> = ({ store }) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const colors = getModernPageColors(theme);
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [skuSearch, setSkuSearch] = useState("");
  const debouncedSku = useDebounce(skuSearch, 400);

  useEffect(() => {
    if (!store.isTransactionsOpen) {
      setSkuSearch("");
      return;
    }
    if ((store.transactionFilters?.sku || "") === debouncedSku.trim()) return;
    store.setTransactionFilters({ sku: debouncedSku.trim() || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSku, store.isTransactionsOpen]);

  const rows = store.transactionRows;
  const totalIn = rows
    .filter((row) => getDirection(row) === "in")
    .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
  const totalOut = rows
    .filter((row) => getDirection(row) === "out")
    .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);

  return (
    <Drawer
      anchor="right"
      open={store.isTransactionsOpen}
      onClose={store.closeTransactions}
      sx={{
        zIndex: 2100,
        "& .MuiDrawer-paper": { zIndex: 2100 },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 560, md: 640 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.mode === "dark" ? "#0F172A" : colors.pageBg,
        }}
      >
        <Box sx={modernDrawerHeaderSx(theme)}>
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography variant="heading4" color={colors.title}>
              Transactions
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.subtitle}
              sx={{ mt: 0.5, wordBreak: "break-word" }}
            >
              {store.transactionsTitle || "—"}
              {store.transactionFilters?.product_id
                ? ` · #${store.transactionFilters.product_id}`
                : ""}
            </CustomTypography>
          </Box>
          <IconButton onClick={store.closeTransactions}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Grid2 container spacing={1.5} sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2 }}>
          <Grid2 size={12}>
            <CustomTextField
              placeholder="Filter by SKU"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              width="100%"
            />
          </Grid2>
          {[
            { label: "Entries", value: store.transactionTotalCount },
            {
              label: isPhone ? "In (page)" : "Stock in (this page)",
              value: `+${totalIn}`,
            },
            {
              label: isPhone ? "Out (page)" : "Stock out (this page)",
              value: `−${totalOut}`,
            },
          ].map((tile) => (
            <Grid2 key={tile.label} size={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${c.softCardBorder}`,
                  bgcolor: c.softCardBackground,
                }}
              >
                <CustomTypography variant="body3" color={c.mutedSlateText}>
                  {tile.label}
                </CustomTypography>
                <CustomTypography variant="heading5" color={c.navyText}>
                  {tile.value}
                </CustomTypography>
              </Paper>
            </Grid2>
          ))}
        </Grid2>

        <Box sx={modernDrawerBodySx()}>
          <InlineFormError message={store.transactionsError} sx={{ mt: 0 }} />
          {store.isTransactionsLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            !store.transactionsError && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: "center",
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <CustomTypography variant="body2" color={colors.subtitle}>
                  No transactions found.
                </CustomTypography>
              </Paper>
            )
          ) : (
            rows.map((row) => <TransactionCard key={row.log_id} row={row} />)
          )}
        </Box>

        {store.transactionTotalCount > 0 && store.transactionFilters && (
          <Box
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: colors.surface,
            }}
          >
            <AppTablePagenation
              fontSize="12px"
              pageNo={store.transactionFilters.page_no}
              limit={PRODUCT_TRANSACTIONS_PAGE_SIZE}
              totalCount={store.transactionTotalCount}
              showRowsPerPageOptions={false}
              showLabelRowsPerPage={false}
              onPageChange={(_event, page) =>
                store.setTransactionFilters({ page_no: page })
              }
              handleRowsChange={() => undefined}
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default observer(SwageazyInventoryTransactionsDrawer);
